










































































































































import {
  defineComponent,
  ref,
  reactive,
  PropType,
  watch,
  Ref,
  computed,
} from "@vue/composition-api";

import store from "@/store";
import BaseInputField from "@/shared/components/base/BaseInputField/BaseInputField.vue";
import LoadingButton from "@/shared/components/LoadingButton";
import { ValidationObserver } from "vee-validate";
import { METER_READING_DATA_KEYS, MeterReadingData } from "../state";
import { getParsedUsage, checkIsPlausible } from "../helpers";

export default defineComponent({
  name: "MeterReadingLoginForm",
  props: {
    meterReading: {
      type: Object as PropType<MeterReadingData>,
      required: true,
    },
    succeeded: {
      type: Boolean,
      default: false,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
    validateNow: {
      type: Number,
      default: 0,
    },
  },
  components: {
    BaseInputField,
    LoadingButton,
    ValidationObserver,
  },
  setup(props: any, ctx: any) {
    // Variables from meter reading prop
    let numberOfDecimals =
      props.meterReading[METER_READING_DATA_KEYS.DECIMAL_POINT] ?? 0;
    const preCommaDigits =
      props.meterReading[METER_READING_DATA_KEYS.PRE_COMMA] ?? 0;
    const min = props.meterReading[METER_READING_DATA_KEYS.READING_VALUE_MIN];
    const max = props.meterReading[METER_READING_DATA_KEYS.READING_VALUE_MAX];
    const range =
      props.meterReading[METER_READING_DATA_KEYS.PLAUSIBLE_RANGE] ?? 0;
    const isGas =
      props.meterReading[METER_READING_DATA_KEYS.SPARTE]?.toLowerCase() ===
      "gas";
    // Max allowed value based on vorkomma/nachkomma digits (e.g. vorkomma=6, nachkomma=0 → 999999)
    const maxAllowedValue = parseFloat(
      "9".repeat(preCommaDigits || 1) +
        (numberOfDecimals > 0 ? "." + "9".repeat(numberOfDecimals) : "")
    );

    const isPlausible = ref(true);

    const parsedOldReadingValue = getParsedUsage(
      props.meterReading[METER_READING_DATA_KEYS.READING_VALUE_OLD],
      numberOfDecimals
    )?.replace(".", ",");
    const formattedCurrentUsage = computed(() =>
      getParsedUsage(
        formData[METER_READING_DATA_KEYS.READING_VALUE_NEW],
        numberOfDecimals
      )?.replace(".", ",")
    );

    const readingValueTitle = isGas
      ? "Aktueller Zählerstand ohne Nachkommastellen"
      : "Aktueller Zählerstand, inklusive Nachkommastellen";

    // Actions from store
    const { updateMeterReadingData } = store.dispatch;
    // Variables for input field
    let customErrors: Ref<string[]> = ref([]);
    const step = `.${"1".padStart(numberOfDecimals, "0")}`;
    const rules = `required`;
    const isUpdatingData = ref(false);
    // Create form data
    const formData = reactive({
      [METER_READING_DATA_KEYS.READING_VALUE_NEW]: "",
      [METER_READING_DATA_KEYS.IMAGE]:
        props.meterReading[METER_READING_DATA_KEYS.IMAGE],
    });
    // Input is valid if:
    // There are no errors, there is a reading value
    // and the input is plausible, or it is not plausible but an image has been added
    const showErrors = ref(false);
    const imageName = ref("");

    const isValid = computed(
      () =>
        !!formData[METER_READING_DATA_KEYS.READING_VALUE_NEW] &&
        !customErrors.value?.length &&
        (isPlausible.value ||
          (!isPlausible.value && !!formData[METER_READING_DATA_KEYS.IMAGE]))
    );

    const emitValidity = () => {
      if (isValid.value) {
        ctx.emit(
          "remove-invalid-reading",
          props.meterReading[METER_READING_DATA_KEYS.ROW_ID]
        );
      } else {
        ctx.emit(
          "add-invalid-reading",
          props.meterReading[METER_READING_DATA_KEYS.ROW_ID]
        );
      }
    };

    // Change handler for usage input
    const handleUsageChange = (current: string, isEmptyValue: boolean) => {
      // Generate custom errors for Meter Readings
      let errors: string[] = [];
      const parsed = getParsedUsage(current, numberOfDecimals);
      const parsedValue = parseFloat(parsed);

      if (!current) {
        ctx.emit(
          "add-invalid-reading",
          props.meterReading[METER_READING_DATA_KEYS.ROW_ID]
        );
        if (isEmptyValue) {
          errors.push("Darf nicht leer sein");
        }
      } else if (isGas && /[.,]/.test(current)) {
        errors.push(
          "Bitte tragen Sie Ihren aktuellen Zählerstand ohne Nachkommastellen ein"
        );
      } else if (!Number.isNaN(parsedValue) && parsedValue > maxAllowedValue) {
        errors.push(
          `Der Wert darf maximal ${preCommaDigits} Vorkommastellen haben`
        );
      }

      // Set plausibility of reading data
      isPlausible.value = checkIsPlausible(parsed, min, max, range);

      customErrors.value = errors;

      emitValidity();
    };

    const handleFormattedUsageUpdate = async (
      current: any,
      isEmptyValue: boolean
    ) => {
      handleUsageChange(current, isEmptyValue);
      current = getParsedUsage(current, numberOfDecimals);
      isUpdatingData.value = true;
      formData[METER_READING_DATA_KEYS.READING_VALUE_NEW] = current;

      const newData: MeterReadingData = {
        ...props.meterReading,
        [METER_READING_DATA_KEYS.READING_VALUE_NEW]: current,
        [METER_READING_DATA_KEYS.IS_PLAUSIBLE]: isPlausible.value,
      };

      await updateMeterReadingData(newData);
      isUpdatingData.value = false;
    };

    // Watch validateNow prop to trigger validation and formatting on submit
    watch(
      () => props.validateNow,
      async (current) => {
        if (current > 0) {
          showErrors.value = true;
          const value = formData[METER_READING_DATA_KEYS.READING_VALUE_NEW];
          await handleFormattedUsageUpdate(value, !!value);
        }
      }
    );

    watch(
      () => formData[METER_READING_DATA_KEYS.IMAGE],
      async (current) => {
        isUpdatingData.value = true;
        formData[METER_READING_DATA_KEYS.IMAGE] = current;

        const newData: MeterReadingData = {
          ...props.meterReading,
          [METER_READING_DATA_KEYS.IMAGE]: current,
        };

        emitValidity();
        await updateMeterReadingData(newData);
        isUpdatingData.value = false;
      }
    );

    const handleImage = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      const image = files ? files[0] : undefined;
      imageName.value = image?.name ?? "";
      createBase64Image(image);
    };

    const createBase64Image = (file?: File) => {
      if (file) {
        const reader = new FileReader();

        reader.onload = (e) => {
          formData[METER_READING_DATA_KEYS.IMAGE] = e.target?.result ?? "";
        };
        reader.readAsDataURL(file);
      }
    };

    return {
      formData,
      min,
      max,
      rules,
      step,
      parsedOldReadingValue,
      formattedCurrentUsage,
      readingValueTitle,
      meterData: props.meterReading,
      METER_READING_DATA_KEYS,
      isUpdatingData,
      customErrors,
      isPlausible,
      isValid,
      handleImage,
      imageName,
      showErrors,
    };
  },
});
